import { takeEvery, select, fork, call, takeLatest } from 'redux-saga/effects'

import * as settings from 'settings'
import { LOCATION_CHANGE, pathSelector } from 'browser-location'
import { PURCHASE_COMPLETED } from 'basket/constants'
import { userSelector, accountKeySelector } from 'auth/selectors'
import { SET_AUTHENTICATED_USER } from 'auth/constants'
import { cookiesAcceptedSelector } from 'cookies/selectors'
import { SET_COOKIES } from 'cookies/constants'
import { deleteCookie } from 'cookies/utils'
import { CONFIGURATION_RECEIVED } from 'wordpress/constants'
import { RD_ANALYTICS_ID } from 'analytics/constants'

import * as constants from './constants'
import * as api from './api'

function hashCode(s) {
    return s.split('').reduce(function (a, b) {
        a = (a << 5) - a + b.charCodeAt(0)
        return a & a
    }, 0)
}

export const getCookie = name => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
        return parts.pop().split(';').shift()
    }
    return null
}

function loadGTag(tagId) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.async = true
        script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
    })
}

function initializeGTag(tagId) {
    window.dataLayer = window.dataLayer || []
    window.gtag = function () {
        window.dataLayer.push(arguments)
    }
    window.gtag('js', new Date())
    window.gtag('config', tagId)
}

/* GTM script provided by Google, no need to rewrite it */
const initGTM = function (w, d, s, l, i) {
    w[l] = w[l] || []
    w[l].push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
    })
    const f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l !== 'dataLayer' ? `&l=${l}` : ''
    j.async = true
    j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`
    f.parentNode.insertBefore(j, f)
}

const initRD = url => {
    const elem = document.getElementById(RD_ANALYTICS_ID)

    if (!elem) {
        const s = document.createElement('script')
        s.type = 'text/javascript'
        s.src = url
        s.async = 1
        s.id = RD_ANALYTICS_ID

        document.body.appendChild(s)
    }
}

function* trackPageView() {
    if (!window.gtag) return
    const path = yield select(pathSelector)
    window.gtag('event', 'page_view', { page_path: path })
}

function* watchLocationChangeSaga() {
    yield takeEvery(LOCATION_CHANGE, trackPageView)
}

function* setUserIdTrackerSaga() {
    yield takeEvery(SET_AUTHENTICATED_USER, function* () {
        if (!window.gtag) return
        const accountKey = yield select(accountKeySelector)
        const tagManagerId = yield select(settings.selectors.googleTagManagerIdSelector)

        //gtag

        window.gtag('event', 'user-id available', {
            send_to: tagManagerId,
            user_id: accountKey,
            event_category: 'authentication',
            event_label: accountKey,
        })
        //gtm
        if (tagManagerId) {
            window.dataLayer.push({ userId: accountKey, event: 'authentication' })
        }
    })
}

function* analyticsSaga() {
    const cookiesAccepted = yield select(cookiesAcceptedSelector)
    if (cookiesAccepted?.analytics) {
        // Google
        const tagManagerId = yield select(settings.selectors.googleTagManagerIdSelector)
        yield call(loadGTag, tagManagerId)
        initializeGTag(tagManagerId)
        yield call(trackPageView)

        if (tagManagerId) {
            initGTM(window, document, 'script', 'dataLayer', tagManagerId)
        }

        const rdAnalyticsUrl = yield select(settings.selectors.rdAnalyticsUrlSelector)

        // RD station
        if (rdAnalyticsUrl) {
            initRD(rdAnalyticsUrl)
        }
    } else if (!!window.gtag && !cookiesAccepted?.analytics) {
        if (window.gtag) window.gtag('remove')
        deleteCookie('_gtag')
        deleteCookie('_gid')
        deleteCookie('_gat')

        // Remove RD analytics
        const elem = document.getElementById(RD_ANALYTICS_ID)
        if (elem) {
            elem.parentNode.removeChild(elem)
        }
    }
}

function* googleReserveConversionSaga() {
    yield takeEvery(constants.SEND_GOOGLE_RESERVE_CONVERSION_DATA, function* ({ currentLocationId }) {
        const rwgToken = getCookie('_rwg_token')
        const merchantId = getCookie('_merchant_id')
        const merchantChanged = merchantId === currentLocationId ? 2 : 1

        if (rwgToken) {
            yield call(api.sendGoogleReserveConversionData, rwgToken, merchantChanged)
        }
    })
}

export default function* rootSaga() {
    yield fork(watchLocationChangeSaga)
    yield fork(setUserIdTrackerSaga)
    yield fork(googleReserveConversionSaga)
    yield takeEvery(SET_COOKIES, analyticsSaga)
    yield takeLatest(CONFIGURATION_RECEIVED, analyticsSaga)
}
